import React, { Component, Fragment } from 'react';
import withStyles from '@mui/styles/withStyles';
import { BILL_TO_OPTIONS } from './BillingConstants';
import Grid from '../../../component/form/Grid';
import TextField from '../../../component/form/TextField';
import PhoneNumberList from '../../../component/form/PhoneNumberList';
import { InlineField, InlineHeaderRightAlignChildren } from '../../../component/form/Inline';
import RadioGroup from '../../../component/form/RadioGroup';
import AddressAutocomplete from '../../../component/form/AddressAutocomplete';
import AddressBookAutocomplete, { getAddressBookById } from '../../../component/form/AddressBookAutocomplete';
import FamilyMemberAutocomplete from '../../../component/form/FamilyMemberAutocomplete';
import DisplayContact from '../../../component/bookings/DisplayContact';
import { IconButton, Typography } from '@mui/material';
import DeleteIcon from '../../../component/icon/DeleteIcon';
import Label from '../../../component/form/Label';
import { joinDefined, stringIsNullOrEmpty } from '../../../util/strings';
import { isNullOrUndefined } from '../../../util/objects';
import ValidationPlaceholder from '../../../component/form/ValidationPlaceholder';
import { ADDRESS_BOOK_CATEGORY } from '../funeralConstants';

export const BILL_TO_MAPPING = {
    billingSource: 'billingSource',
    relationToDeceased: 'relationToDeceased',
    fullName: 'fullName',
    givenName: 'givenName',
    middleName: 'middleName',
    surname: 'surname',
    phones: 'phones',
    email: 'email',
    fax: 'fax',
    line1: 'line1',
    line2: 'line2',
    city: 'city',
    state: 'state',
    country: 'country',
    code: 'code',
    billingId: 'billingId'
};

class BillTo extends Component {
    state = {
        hasBillingTarget: false,
        billingContact: null,
        relationToDeceased: null,
        addressMapping: null
    };

    static getDerivedStateFromProps({ mapping }, { addressMapping }) {
        if (!isNullOrUndefined(addressMapping)) return null;
        return {
            addressMapping: {
                line1: mapping.line1,
                line2: mapping.line2,
                city: mapping.city,
                state: mapping.state,
                country: mapping.country,
                code: mapping.code
            }
        };
    }

    componentDidMount() {
        const { billingContact } = this.state;
        const { mapping, billingTargetForm } = this.props;
        const billingSource = billingTargetForm.getField(mapping.billingSource);
        const billingId = billingTargetForm.getField(mapping.billingId);

        if (billingSource === 'AddressBook' && isNullOrUndefined(billingContact) && !isNullOrUndefined(billingId)) {
            getAddressBookById(billingId).then(x => {
                this.setState({ billingContact: x });
                billingTargetForm.setField({ [mapping.billingId]: x.ID });
            });
        }
    }

    render() {
        const { disabled, billingTargetForm, mapping, billersOnly } = this.props;
        const billingSource = billingTargetForm.getField(mapping.billingSource);

        return (
            <Fragment>
                <InlineHeaderRightAlignChildren header={'Bill To'}>
                    <RadioGroup
                        validationResult={billingTargetForm.getValidation(mapping.billingSource)}
                        disabled={disabled}
                        options={
                            billersOnly
                                ? BILL_TO_OPTIONS.map(e => ({
                                      ...e,
                                      label: 'AddressBook' === e.value ? 'Billing Account' : e.label
                                  }))
                                : BILL_TO_OPTIONS
                        }
                        value={billingSource}
                        onChange={e => this.onBillingSourceChanged(e.target.value)}
                    />
                </InlineHeaderRightAlignChildren>
                {stringIsNullOrEmpty(billingSource) && this.renderEmpty()}
                {billingSource === 'Applicant' && this.renderApplicantDetails()}
                {billingSource === 'AddressBook' && this.renderAddressBook()}
                {billingSource === 'FamilyMember' && this.renderFamilyMember()}
                {billingSource === 'Other' && this.renderOtherBillingTarget()}
            </Fragment>
        );
    }

    renderEmpty() {
        return (
            <Grid item xs={12}>
                <Typography variant="caption">Please select a billing source...</Typography>
            </Grid>
        );
    }

    renderApplicantDetails() {
        return this.renderBillingTarget(false, false);
    }

    renderAddressBook() {
        const { billingContact } = this.state;
        const { billingTargetForm, classes, billersOnly } = this.props;

        return (
            <Fragment>
                {!billingContact ? (
                    <ValidationPlaceholder name={'BillTo.BillingId'} form={billingTargetForm}>
                        <InlineField>
                            <AddressBookAutocomplete
                                clearOnSelect={true}
                                placeholder={
                                    billersOnly ? 'Search for a Billing Account' : 'Search for any billable supplier'
                                }
                                categories={
                                    billersOnly && [ADDRESS_BOOK_CATEGORY.billing, ADDRESS_BOOK_CATEGORY.funeralFund]
                                }
                                onSelect={(_, result) => this.onSelectBillerFromAddressBook(result)}
                                value={billingContact}
                            />
                        </InlineField>
                    </ValidationPlaceholder>
                ) : (
                    <div className={classes.listItem}>
                        <span style={{ float: 'right' }}>
                            <IconButton
                                title={'Remove'}
                                onClick={() => {
                                    this.onBillingSourceChanged('AddressBook');
                                }}
                                size="large"
                            >
                                <DeleteIcon />
                            </IconButton>
                        </span>
                        <p>
                            <Label text={'Selected ' + (billersOnly ? 'Billing Account' : 'Supplier')} />
                        </p>
                        <DisplayContact contactID={billingContact.ID} defaultExpanded />
                    </div>
                )}
            </Fragment>
        );
    }

    renderFamilyMember() {
        const { billingSourceForm, billingTargetForm, mapping, classes } = this.props;
        const funeralId = billingSourceForm.getField('ID');
        const familyMemberName = joinDefined(
            [
                billingTargetForm.getField(mapping.givenName),
                billingTargetForm.getField(mapping.middleName),
                billingTargetForm.getField(mapping.surname)
            ],
            ' '
        );
        const familyMember = familyMemberName
            ? familyMemberName + ' (' + (billingTargetForm.getField(mapping.relationToDeceased) || 'custom') + ')'
            : '';

        return (
            <div className={classes.listItem}>
                <Grid container spacing={1}>
                    <Grid xs={12}>
                        <FamilyMemberAutocomplete
                            name="ApplicantFamilyMember_Billing"
                            onChange={this.onChangeBillingTargetFamilyMember}
                            funeralId={funeralId}
                            value={familyMember}
                        />
                    </Grid>
                    {this.renderBillingTarget(true, true)}
                </Grid>
            </div>
        );
    }

    renderOtherBillingTarget() {
        return this.renderBillingTarget(true, true);
    }

    renderBillingTarget(canEditNames, canEditContactDetails) {
        const { billingTargetForm, classes, mapping, disabled } = this.props;
        const { addressMapping } = this.state;
        const fullName = billingTargetForm.getField(mapping.fullName);

        return (
            <Fragment>
                {!stringIsNullOrEmpty(fullName) ? (
                    <Grid item xs={12} md={12}>
                        <TextField
                            titleCase
                            name={mapping.fullName}
                            label="Name"
                            form={billingTargetForm}
                            disabled={!canEditNames || disabled}
                        />
                    </Grid>
                ) : (
                    <Fragment>
                        <Grid item xs={12} md={4}>
                            <TextField
                                titleCase
                                name={mapping.givenName}
                                label="First name"
                                form={billingTargetForm}
                                disabled={!canEditNames || disabled}
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <TextField
                                titleCase
                                name={mapping.middleName}
                                label="Middle Name(s) (optional)"
                                form={billingTargetForm}
                                disabled={!canEditNames || disabled}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                titleCase
                                name={mapping.surname}
                                label="Surname"
                                form={billingTargetForm}
                                disabled={!canEditNames || disabled}
                            />
                        </Grid>
                    </Fragment>
                )}

                <Grid
                    item
                    xs={12}
                    header="Billing Address"
                    className={classes.col2}
                    disabled={!canEditContactDetails || disabled}
                >
                    <AddressAutocomplete
                        label="Billing Address"
                        id="BillingAddress"
                        placeholder="Enter an address..."
                        componentFields={addressMapping}
                        form={billingTargetForm}
                        disabled={!canEditContactDetails || disabled}
                        allowCustomAddress={!!canEditContactDetails}
                        required
                    />
                </Grid>

                <PhoneNumberList
                    name={mapping.phones}
                    label="Phone"
                    form={billingTargetForm}
                    disabled={!canEditContactDetails || disabled}
                />
                <Grid item xs={12}>
                    <TextField
                        className={classes.alignWithLabel}
                        name={mapping.email}
                        label="Email (optional)"
                        type="email"
                        form={billingTargetForm}
                        disabled={!canEditContactDetails || disabled}
                    />
                </Grid>
            </Fragment>
        );
    }

    onBillingSourceChanged(billingSource) {
        const { mapping, billingSourceForm, billingTargetForm } = this.props;
        let hasBillingTarget = false;

        if (billingSource === 'Applicant') {
            const infFullNamePiece = billingSourceForm.getField('Informant.InformantsName') || '';
            let infSurname = billingSourceForm.getField('Informant.Surname');
            let infFirstName = billingSourceForm.getField('Informant.FirstName');
            let infMiddleName = billingSourceForm.getField('Informant.MiddleName');
            // need to reconstruct informant name from legacy data...?
            if (!!infFullNamePiece && !infSurname && !infFirstName && !infMiddleName) {
                const bits = ('' + infFullNamePiece).split(' ');
                if (!infSurname) infSurname = bits.pop();
                else bits.pop();
                if (!infFirstName) infFirstName = bits.shift();
                else bits.shift();
                if (!infMiddleName) infMiddleName = bits.join(' ');
            }

            const infFullName = infFullNamePiece || joinDefined([infFirstName, infMiddleName, infSurname], ' ');

            let state = {
                [mapping.givenName]: infFirstName,
                [mapping.middleName]: infMiddleName,
                [mapping.surname]: infSurname,
                [mapping.fullName]: infFullName,
                [mapping.email]: billingSourceForm.getField('Informant.Email'),
                [mapping.relationToDeceased]: billingSourceForm.getField('Informant.RelationshipToDeceased'),
                [mapping.billingSource]: billingSource
            };

            this.applyPhonesToState(state, this.buildPhoneEntries(billingSourceForm.getField('Informant.Phones')));

            const useResidential = billingSourceForm.getField('Informant.PostalSameAsResidential');
            if (useResidential) {
                state[mapping.line1] = billingSourceForm.getField('Informant.ResidentialAddress1');
                state[mapping.line2] = billingSourceForm.getField('Informant.ResidentialAddress2') || null;
                state[mapping.city] = billingSourceForm.getField('Informant.SuburbCityAust');
                state[mapping.state] = billingSourceForm.getField('Informant.ResidentialState');
                state[mapping.code] = billingSourceForm.getField('Informant.ResidentialPostcode');
                state[mapping.country] = null;
            } else {
                state[mapping.line1] = billingSourceForm.getField('Informant.PostalAddressLine1');
                state[mapping.line2] = billingSourceForm.getField('Informant.PostalAddressLine2');
                state[mapping.city] = billingSourceForm.getField('Informant.PostalSuburb');
                state[mapping.state] = billingSourceForm.getField('Informant.PostalState');
                state[mapping.code] = billingSourceForm.getField('Informant.PostalPostcode');
                state[mapping.country] = billingSourceForm.getField('Informant.PostalCountry');
            }

            billingTargetForm.setField(state);
            hasBillingTarget = true;
        } else if (billingSource === 'AddressBook') {
            this.clearBillingTarget(billingSource);
        } else if (billingSource === 'FamilyMember') {
            this.clearBillingTarget(billingSource);
        } else if (billingSource === 'Other') {
            this.clearBillingTarget(billingSource);
            hasBillingTarget = true;
        } else {
            this.clearBillingTarget();
            hasBillingTarget = false;
        }

        this.setState({ billingContact: null, hasBillingTarget });
    }

    buildPhoneEntries(entries) {
        return (entries || [])
            .filter(entry => !!entry && !!String(entry.Number || '').trim())
            .map((entry, index) => ({
                Number: entry.Number,
                Type: entry.Type || null,
                Comment: entry.Comment || null,
                Sort: index
            }));
    }

    applyPhonesToState(state, entries) {
        const { mapping } = this.props;
        state[mapping.phones] = entries;
        return state;
    }

    clearBillingTarget(billingSource = '') {
        const { mapping, billingTargetForm } = this.props;
        let state = {
            [mapping.relationToDeceased]: null,
            [mapping.givenName]: null,
            [mapping.middleName]: null,
            [mapping.surname]: null,
            [mapping.fullName]: null,
            [mapping.phones]: [],
            [mapping.email]: null,
            [mapping.line1]: null,
            [mapping.line2]: null,
            [mapping.city]: null,
            [mapping.state]: null,
            [mapping.code]: null,
            [mapping.country]: null,
            [mapping.billingSource]: billingSource
        };

        if (!isNullOrUndefined(mapping.billingId)) state[mapping.billingId] = null;

        billingTargetForm.setField(state);
    }

    onSelectBillerFromAddressBook(contact) {
        const { billingTargetForm, mapping } = this.props;
        let state = {
            [mapping.relationToDeceased]: null,
            [mapping.fullName]: contact.Name,
            [mapping.givenName]: '',
            [mapping.middleName]: '',
            [mapping.surname]: '',
            [mapping.email]: contact.Email,
            [mapping.line1]: contact.AddressLine1,
            [mapping.line2]: contact.AddressLine2,
            [mapping.city]: contact.Suburb,
            [mapping.state]: contact.State,
            [mapping.code]: contact.Postcode,
            [mapping.country]: null,
            [mapping.billingSource]: 'AddressBook'
        };

        this.applyPhonesToState(
            state,
            this.buildPhoneEntries([
                { Number: contact.Phone },
                { Number: contact.Mobile, Type: 'Mobile' }
            ])
        );

        if (!isNullOrUndefined(mapping.billingId)) state[mapping.billingId] = contact.ID;
        billingTargetForm.setField(state);
        this.setState({ billingContact: contact, hasBillingTarget: true });
    }

    onChangeBillingTargetFamilyMember = item => {
        if (item && item.metadata) {
            const { source, relationship } = item.metadata;
            const { billingTargetForm, mapping } = this.props;
            let state = {
                [mapping.relationToDeceased]: relationship,
                [mapping.givenName]: source.FirstName,
                [mapping.middleName]: source.MiddleName,
                [mapping.surname]: source.Surname,
                [mapping.phones]: [],
                [mapping.email]: null,
                [mapping.line1]: null,
                [mapping.line2]: null,
                [mapping.city]: null,
                [mapping.state]: null,
                [mapping.code]: null,
                [mapping.country]: null,
                [mapping.billingSource]: 'FamilyMember'
            };

            if (!isNullOrUndefined(mapping.billingId)) state[mapping.billingId] = null;
            billingTargetForm.setField(state);
            this.setState({ hasBillingTarget: true });
        } else {
            this.clearBillingTarget('FamilyMember');
            this.setState({ hasBillingTarget: false });
        }
    };
}

const styles = {
    alignWithLabel: {
        // marginTop: 10
    },
    listItem: {
        width: '100%',
        // maxWidth: 600,
        padding: '0 8px 8px 8px',
        margin: '6px 0 0 !important',
        borderRadius: 5,
        // backgroundColor: 'rgba(0,0,0,0.035)',
        '&:hover': {
            backgroundColor: 'rgba(0,0,0,0.05)'
        },
        '& > p': {
            margin: '0.65rem 0'
        }
    }
};

export default withStyles(styles)(BillTo);
